import React from "react";

import Seo from "../seo/Seo";
import { buildLandingGraph } from "../seo/structuredData";
import LandingPage, {
  getLandingPage,
  breadcrumbTrail,
} from "../pages/LandingPage";
import { pages as landingPageList } from "../data/landingPages";
import { SITE_URL } from "../data/profile";

/**
 * publicRoutes
 *
 * Landing pages are generated from src/data/landingPages.json rather than
 * hand-written per route. One route definition means one place where a
 * canonical, an OG tag or a JSON-LD graph can be wrong — instead of twelve.
 *
 * api/ssr.js reads the same JSON to server-render these URLs, so the HTML a
 * crawler receives matches what React hydrates.
 */

/** Every landing route, ready to be dropped into <Routes>. */
export const landingRoutes = landingPageList.map((page) => ({
  path: `/${page.slug}`,
  slug: page.slug,
  element: (
    <>
      <Seo
        path={`/${page.slug}`}
        type="website"
        title={page.meta.title}
        description={page.meta.description}
        graph={buildLandingGraph(page, { trail: breadcrumbTrail(page) })}
      />
      <LandingPage slug={page.slug} />
    </>
  ),
}));

/** Slugs only — used by the sitemap builder and the nav. */
export const landingSlugs = landingPageList.map((p) => p.slug);

/**
 * The pages that belong in the sitemap, with the priority/changefreq the
 * generator needs. Kept beside the route definitions so a new landing page
 * cannot be added without also being discoverable.
 */
export function sitemapEntries() {
  const staticPages = [
    { loc: "/", priority: "1.0", changefreq: "weekly" },
    { loc: "/projects", priority: "0.9", changefreq: "weekly" },
    { loc: "/blogs", priority: "0.7", changefreq: "weekly" },
    { loc: "/contact", priority: "0.7", changefreq: "monthly" },
  ];

  const landingPages = landingPageList.map((p) => ({
    loc: `/${p.slug}`,
    // Commercial pages carry the conversion intent, so they outrank the
    // explainers that support them.
    priority: p.kind === "hire" || p.kind === "hub" ? "0.9" : "0.8",
    changefreq: "monthly",
  }));

  return [...staticPages, ...landingPages].map((e) => ({
    ...e,
    loc: `${SITE_URL}${e.loc}`,
  }));
}

export { getLandingPage, breadcrumbTrail };
